import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpUpplandsVasbyPage: ServicePageConfig = {
  slug: "flytthjalp-upplands-vasby",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Upplands Väsby – Skåpbil & Bärare | Ungdomskraft",
    description:
      "Flytthjälp och flyttfirma i Upplands Väsby: vi bär, kör och lossar i Runby, Smedby, Bollstanäs, Grimstaby och centrala Väsby. Fast pris, RUT-avdrag.",
    h1: "Flytthjälp Upplands Väsby",
  },

  hero: {
    heading: "Flytthjälp i Upplands Väsby",
    intro:
      "Ska du flytta till, från eller inom Upplands Väsby? Vi kommer med skåpbil och starka ungdomar och tar hela flytten – från radhuset i Grimstaby eller lägenheten i Smedby till villan i Runby, Bollstanäs eller Fresta. Du ser priset när du bokar, RUT-avdraget dras direkt på fakturan och vi har ofta lediga tider redan samma vecka.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt:
      "Ungdomskraft bär möbler till skåpbilen vid en flytt i Upplands Väsby",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Sedan 2014 har Ungdomskraft hjälpt hushåll norr om Stockholm att flytta utan att behöva låna släpvagn eller tjata på vänner. I Upplands Väsby bär vi allt från radhusbohag i Grimstaby till villaförråd i Runby – med lyftteknik, filtar och en skåpbil som står redo vid porten.",
    bookYourText: "Boka flytten i Väsby",
    bookDescText:
      "Skriv in båda adresserna, lista det som ska med och välj dag. Priset visas direkt – bärare och skåpbil ingår.",
    vehicleTitleText: "Vi kommer med bilen",
    vehicleDescText:
      "Ingen hyrbil att hämta i Infra City och ingen släpkärra att backa in på uppfarten. Vi kör skåpbilen, lastar den tätt och säkert och lossar på nya adressen – oavsett om den ligger i Väsby eller i andra änden av länet.",
  },

  itemsSection: {
    heading: "Det här flyttar vi oftast i Upplands Väsby",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Hörnsoffor och fåtöljer som ska ut ur radhus och villor",
        image: "/sofa.webp",
        alt: "Soffa som bärs ut vid flytthjälp i Upplands Väsby",
      },
      {
        name: "Sängar & madrasser",
        description: "Kontinentalsängar, våningssängar och barnsängar",
        image: "/bed.webp",
        alt: "Säng som lastas i skåpbil vid flytt i Runby, Upplands Väsby",
      },
      {
        name: "Bord & stolar",
        description: "Matgrupper, skrivbord och trädgårdsmöbler från altanen",
        image: "/table.webp",
        alt: "Matbord och stolar vid flytt från villa i Bollstanäs",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare, kyl och frys från tvättstuga eller källare",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas vid flytthjälp i Smedby, Upplands Väsby",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober som tas isär, byråer, bokhyllor och tv-bänkar",
        image: "/wardrobe.webp",
        alt: "Garderob som demonteras vid flytt i Upplands Väsby",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger, förrådslådor och säckar från vind och garage",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs ut vid flytt i Grimstaby, Upplands Väsby",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Upplands Väsby – från radhus i Grimstaby till villa i Runby",
    mainText:
      "Upplands Väsby är en småstad med blandad bebyggelse, och det märks när man flyttar. Smedby och Ekebo byggdes från mitten av 1970-talet med hyreshus runt gårdar, Grimstaby är ett radhusområde från samma tid som ritades som en liten stad med egen huvudgata och torg, och i Carlslund öster om E4 står 1950-talshus. Runt dem ligger villaområden som Runby, Bollstanäs, Brunnby och Fresta, där flytten ofta innebär ett fullt garage, en källare och tunga trädgårdsmöbler. Lägenhetsflytten i Smedby handlar om hiss, trapphus och avstånd från porten till bilen. Villaflytten i Runby handlar om mängd, tunga vitvaror och många vändor mellan huset och skåpbilen.\n\nMånga som flyttar hit gör det för pendlingen. Upplands Väsby station ligger på pendeltågslinjerna mot Stockholm City och Södertälje och direkt norrut mot Arlanda och Uppsala, och E4 går rakt genom kommunen. Det gör Väsby populärt bland barnfamiljer som lämnar en lägenhet i Stockholm för radhus eller villa, men också bland dem som flyttar ihop, separerar eller byter en villa i Runby mot en lägenhet nära centrum. Kring stationen planerar kommunen dessutom en ny stadsdel under namnet Väsby Entré, så fler lägenheter nära spåren är på väg.\n\nEftersom E4 och Norrortsleden möts strax söder om Väsby når vi både Stockholms innerstad, Sollentuna, Täby och Märsta utan omvägar. Vi planerar lastningen efter hur din adress ser ut: gästparkering på gården i Smedby, uppfart vid villan i Bollstanäs eller en smal gata i Grimstaby där bilen behöver stå nära huset. En vanlig flytt i Väsby består av två bärare och en skåpbil. Stora villaflyttar tar vi i flera vändor eller med fler bärare, så att allt är på plats samma dag.",
    preparationHeading: "Så förbereder du flytten i Upplands Väsby",
    preparationText:
      "Packa kartongerna i förväg och skriv rum och innehåll på sidan, inte bara på locket, så syns det även när de står staplade. Töm frysen dagen innan och låt den stå öppen så att den hinner torka. Har du en PAX-garderob eller en stor säng som inte går genom dörren hel kan vi ta isär den på plats. Lägg skruvar och beslag i en märkt påse och tejpa fast den på delen.\n\nBor du i villa eller radhus hjälper det om uppfarten är fri så att skåpbilen kan backa nära entrén. Flyttar du från en lägenhet i Smedby, Ekebo eller centrala Väsby, kolla om det finns en gästparkering eller lastplats nära porten och om hissen går att låsa medan vi bär. Skriv det i bokningen, tillsammans med portkoder och om det finns förråd i källaren eller på vinden som också ska med.",
    additionalSections: [
      {
        heading: "Områden i Upplands Väsby där vi hjälper till med flytt",
        content:
          "Vi flyttar hushåll i hela Upplands Väsby kommun. I de centrala delarna kring stationen och Väsby centrum, samt i Smedby, Ekebo och Carlslund, bär vi i flerbostadshus med och utan hiss. I Grimstaby och andra radhusområden är det ofta nära mellan dörr och bil men trånga trappor inne i huset.\n\nI villaområdena Runby, Bollstanäs, Brunnby, Fresta och Sigma tömmer vi hela hus med källare, vind och garage. Vi hjälper också företag i Infra City och övriga verksamhetsområden med mindre kontorsflyttar.\n\nKörsträcka inom 20 km ingår i alla paket. Flyttar du längre, till exempel från Väsby till södra Stockholm, räknas resten per kilometer enligt paketets pris och syns direkt när du bokar.",
      },
      {
        heading: "Flytt till eller från Upplands Väsby",
        content:
          "Många av våra flyttar i Väsby går över kommungränsen. En barnfamilj flyttar från en trea i Solna till ett radhus i Grimstaby, ett par byter villan i Runby mot en lägenhet i Sollentuna, eller en student lämnar Väsby för en korridor i Uppsala. Vi kör hela sträckan själva och planerar dagen så att bilen inte står still i E4-köerna i rusningstid.\n\nFlytthjälp ger rätt till RUT-avdrag. Du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år, och vi drar av det direkt på fakturan.",
        cta: { text: "Se flytthjälp i grannorten Märsta →", href: "/flytthjalp-marsta" },
      },
      {
        heading: "Varför boka flytthjälp i Upplands Väsby med Ungdomskraft?",
        listItems: [
          {
            label: "Vi känner Väsby –",
            text: "Gårdarna i Smedby, gatorna i Grimstaby och uppfarterna i Runby och Bollstanäs. Vi vet var bilen ska stå och hur lång bärsträckan blir.",
          },
          {
            label: "Nära till E4 och Norrortsleden –",
            text: "Vi har bilar och personal i hela Stockholmsregionen och tar oss snabbt till Väsby, ofta samma vecka och ibland samma dag.",
          },
          {
            label: "Pris innan du bokar –",
            text: "Du ser totalpriset i bokningen. Ingen offertrunda, inga påslag för trappor och RUT-avdraget dras direkt.",
          },
          {
            label: "Försäkrat –",
            text: "Alla flyttar är försäkrade genom Trygg Hansa, både ditt bohag och trapphuset eller huset vi bär i.",
          },
          {
            label: "Skåpbil, filtar och remmar ingår –",
            text: "Du behöver inte hyra bil eller köpa skydd. Vi har med allt som behövs för att flytten ska gå säkert.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Upplands Väsby?",
      answer:
        "Det beror på hur mycket som ska flyttas. Några möbler från en etta ryms ofta i Liten (995 kr) eller Mellan (1 495 kr), en fullt möblerad tvåa eller trea brukar landa på 2 495–4 995 kr och ett helt radhus eller en villa bokas som Mega Stor (8 995 kr, upp till 8 timmar). Körsträcka inom 20 km ingår och du ser priset innan du bokar.",
    },
    {
      question: "Är ni en flyttfirma i Upplands Väsby?",
      answer:
        "Ja, vi utför kompletta flyttar i Upplands Väsby med skåpbil och bärare, precis som en flyttfirma. Skillnaden är att du bokar direkt online och ser ett fast pris i stället för att vänta på en offert.",
    },
    {
      question: "Får jag RUT-avdrag på flytten?",
      answer:
        "Ja. Flytthjälp ger 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av avdraget direkt på fakturan, så du betalar bara din del.",
    },
    {
      question: "Hur snabbt kan ni hjälpa mig att flytta i Väsby?",
      answer:
        "Vi har ofta lediga tider samma vecka och ibland samma dag. Välj datum i bokningen så ser du direkt vilka tider som finns.",
    },
    {
      question: "Kan ni flytta en hel villa i Runby eller Bollstanäs?",
      answer:
        "Ja. Vi tar hela huset inklusive källare, vind, garage och trädgårdsmöbler. Stora villor bokas som Extra Stor eller Mega Stor, och vid behov gör vi flera vändor med skåpbilen.",
    },
    {
      question: "Bär ni i trappor utan hiss?",
      answer:
        "Ja. Vi bär i hyreshus utan hiss, i radhusens interna trappor och ner från vindar. Trappor kostar inget extra.",
    },
    {
      question: "Vad kostar det att flytta från Väsby till Stockholm?",
      answer:
        "De första 20 km ingår. Från Upplands Väsby till innerstaden är det längre än så, och kilometrarna därefter räknas enligt paketets km-pris som syns i pristabellen och i bokningen.",
    },
    {
      question: "Kan ni ta isär och montera möbler?",
      answer:
        "Ja, vi demonterar sängar, garderober och bord som inte går ut hela och sätter ihop dem igen på nya adressen.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, tvättmaskin, torktumlare, kyl, frys och diskmaskin. Töm och frosta av kyl och frys minst ett dygn innan så att inget läcker i bilen.",
    },
    {
      question: "Kan ni flytta på helgen?",
      answer:
        "Ja, vi flyttar alla dagar i veckan, även lördag och söndag.",
    },
    {
      question: "Hjälper ni företag i Infra City med flytt?",
      answer:
        "Ja, vi gör mindre kontors- och lagerflyttar i Infra City och resten av Väsby: skrivbord, kontorsstolar, arkivskåp och kartonger.",
    },
    {
      question: "Vilka delar av Upplands Väsby täcker ni?",
      answer:
        "Hela kommunen: centrala Väsby, Smedby, Ekebo, Carlslund, Grimstaby, Runby, Bollstanäs, Brunnby, Fresta, Sigma och Infra City.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Vi hjälper till med flytt i hela Stockholmsregionen: ${stockholmKommunerText}. Körsträcka inom 20 km ingår alltid.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Upplands Väsby i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbilen, skyddar det ömtåliga, bär ut och lastar. Sedan kör vi direkt till nya adressen, i Väsby eller var du nu ska bo.",
    step3Title: "Inflyttat",
    step3Desc:
      "Vi bär in allt, ställer möblerna i rätt rum och sätter ihop det vi tog isär. Kvar för dig är bara att packa upp.",
    ctaHeading: "Boka flytthjälp i Upplands Väsby",
    ctaText:
      "Fyll i gamla och nya adressen, välj paket och tid. Priset syns direkt och RUT-avdraget dras på fakturan.",
  },

  en: {
    slug: "moving-help-upplands-vasby",
    meta: {
      title: "Moving Help Upplands Väsby – Van & Movers | Ungdomskraft",
      description:
        "Moving help in Upplands Väsby: we carry, drive and unload in Runby, Smedby, Bollstanäs, Grimstaby and central Väsby. Fixed price, RUT deduction.",
      h1: "Moving Help Upplands Väsby",
    },
    hero: {
      heading: "Moving Help in Upplands Väsby",
      intro:
        "Moving to, from or within Upplands Väsby? We turn up with a van and strong young movers and handle the whole move — from a terraced house in Grimstaby or a flat in Smedby to a detached house in Runby, Bollstanäs or Fresta. You see the price when you book, the RUT deduction comes off the invoice and we often have slots the same week.",
      heroImageAlt:
        "Ungdomskraft carrying furniture to the van during a move in Upplands Väsby",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Since 2014 Ungdomskraft has helped households north of Stockholm move without borrowing a trailer or begging friends. In Upplands Väsby we carry everything from terraced-house contents in Grimstaby to villa storage in Runby — with proper lifting technique, blankets and a van waiting by the door.",
      bookYourText: "Book your Väsby move",
      bookDescText:
        "Enter both addresses, list what's coming and pick a day. The price shows instantly — movers and van included.",
      vehicleTitleText: "We bring the van",
      vehicleDescText:
        "No rental van to collect in Infra City and no trailer to reverse up the drive. We drive the van, load it tightly and safely and unload at the new address — whether it's in Väsby or across the county.",
    },
    itemsSection: {
      heading: "What we move most often in Upplands Väsby",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Corner sofas and armchairs leaving terraced and detached houses",
          image: "/sofa.webp",
          alt: "Sofa being carried out during a move in Upplands Väsby",
        },
        {
          name: "Beds & mattresses",
          description: "Continental beds, bunk beds and children's beds",
          image: "/bed.webp",
          alt: "Bed loaded into a van during a move in Runby, Upplands Väsby",
        },
        {
          name: "Tables & chairs",
          description: "Dining sets, desks and garden furniture from the deck",
          image: "/table.webp",
          alt: "Dining table and chairs during a house move in Bollstanäs",
        },
        {
          name: "Appliances",
          description: "Washers, dryers, fridges and freezers from laundry room or basement",
          image: "/washer.webp",
          alt: "Washing machine moved during moving help in Smedby, Upplands Väsby",
        },
        {
          name: "Wardrobes & dressers",
          description: "Wardrobes taken apart, dressers, bookcases and TV units",
          image: "/wardrobe.webp",
          alt: "Wardrobe being dismantled during a move in Upplands Väsby",
        },
        {
          name: "Moving boxes",
          description: "Boxes, storage crates and bags from the loft and garage",
          image: "/box.webp",
          alt: "Moving boxes carried out during a move in Grimstaby, Upplands Väsby",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Upplands Väsby — from Grimstaby terraces to Runby houses",
      mainText:
        "Upplands Väsby is a small town with mixed housing, and you notice it when you move. Smedby and Ekebo were built from the mid-1970s with rental blocks around courtyards, Grimstaby is a terraced-house area from the same era designed as a little town with its own main street and square, and Carlslund east of the E4 has 1950s buildings. Around them lie detached-house areas such as Runby, Bollstanäs, Brunnby and Fresta, where a move usually means a full garage, a basement and heavy garden furniture. A flat move in Smedby is about the lift, the stairwell and the distance from the door to the van. A house move in Runby is about volume, heavy appliances and many trips between the house and the van.\n\nMany people move here for the commute. Upplands Väsby station is on the commuter-train lines towards Stockholm City and Södertälje and straight north to Arlanda and Uppsala, and the E4 runs right through the municipality. That makes Väsby popular with families leaving a Stockholm flat for a terraced or detached house, but also with people moving in together, separating or swapping a house in Runby for a flat near the centre. Around the station the municipality is also planning a new district under the name Väsby Entré, so more flats close to the tracks are on the way.\n\nBecause the E4 and Norrortsleden meet just south of Väsby, we reach central Stockholm, Sollentuna, Täby and Märsta without detours. We plan the loading around your address: guest parking in a Smedby courtyard, a driveway in Bollstanäs or a narrow street in Grimstaby where the van needs to stand close. A typical Väsby move is two movers and one van. Large house moves are done in several trips or with more movers so that everything is in place the same day.",
      preparationHeading: "How to prepare your move in Upplands Väsby",
      preparationText:
        "Pack boxes in advance and write the room and contents on the side, not just the lid, so it's visible when they're stacked. Empty the freezer the day before and leave it open to dry. If you have a PAX wardrobe or a large bed that won't fit through the door in one piece, we can take it apart on site. Put screws and fittings in a labelled bag and tape it to the part.\n\nIn a detached or terraced house it helps if the driveway is clear so the van can reverse close to the entrance. Moving out of a flat in Smedby, Ekebo or central Väsby? Check whether there's guest parking or a loading spot near the entrance and whether the lift can be held while we carry. Note it in the booking, along with door codes and any basement or loft storage that's coming too.",
      additionalSections: [
        {
          heading: "Areas in Upplands Väsby where we help you move",
          content:
            "We move households across the whole of Upplands Väsby municipality. Around the station and Väsby centrum, and in Smedby, Ekebo and Carlslund, we carry in apartment blocks with and without lifts. In Grimstaby and other terraced areas the distance from door to van is short, but the stairs inside the house are often tight.\n\nIn the detached-house areas of Runby, Bollstanäs, Brunnby, Fresta and Sigma we clear whole houses with basement, loft and garage. We also help businesses in Infra City and other commercial areas with smaller office moves.\n\nDriving within 20 km is included in every package. For longer trips, for example from Väsby to southern Stockholm, the remaining distance is charged per kilometre at the package rate and shown as soon as you book.",
        },
        {
          heading: "Moving to or from Upplands Väsby",
          content:
            "Many of our Väsby moves cross the municipal border. A family moves from a three-room flat in Solna to a terraced house in Grimstaby, a couple swaps their house in Runby for a flat in Sollentuna, or a student leaves Väsby for a corridor room in Uppsala. We drive the whole distance ourselves and plan the day so the van isn't stuck in rush-hour queues on the E4.\n\nMoving help qualifies for the RUT tax deduction. You get 50% off the labour cost, up to SEK 75,000 per person per year, and we deduct it directly on the invoice.",
          cta: { text: "See moving help in neighbouring Märsta →", href: "/en/moving-help-marsta" },
        },
        {
          heading: "Why book moving help in Upplands Väsby with Ungdomskraft?",
          listItems: [
            {
              label: "We know Väsby –",
              text: "The courtyards of Smedby, the streets of Grimstaby and the driveways of Runby and Bollstanäs. We know where the van should stand and how far we'll be carrying.",
            },
            {
              label: "Close to the E4 and Norrortsleden –",
              text: "We have vans and staff across the Stockholm region and get to Väsby quickly, often the same week and sometimes the same day.",
            },
            {
              label: "Price before you book –",
              text: "You see the total in the booking. No quote round, no surcharge for stairs, and the RUT deduction is applied directly.",
            },
            {
              label: "Insured –",
              text: "Every move is insured through Trygg Hansa, covering both your belongings and the stairwell or house we carry in.",
            },
            {
              label: "Van, blankets and straps included –",
              text: "No need to rent a vehicle or buy protection. We bring everything needed for a safe move.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Upplands Väsby cost?",
        answer:
          "It depends on how much is moving. A few pieces from a studio usually fit Small (SEK 995) or Medium (SEK 1,495), a fully furnished one- or two-bed flat tends to land at SEK 2,495–4,995 and a whole terraced or detached house is booked as Mega Large (SEK 8,995, up to 8 hours). Driving within 20 km is included and you see the price before booking.",
      },
      {
        question: "Are you a removal company in Upplands Väsby?",
        answer:
          "Yes, we carry out complete moves in Upplands Väsby with a van and movers, just like a removal firm. The difference is that you book online and see a fixed price instead of waiting for a quote.",
      },
      {
        question: "Do I get the RUT deduction on my move?",
        answer:
          "Yes. Moving help gives a 50% deduction on the labour cost, up to SEK 75,000 per person per year. We apply it directly on the invoice, so you only pay your share.",
      },
      {
        question: "How quickly can you help me move in Väsby?",
        answer:
          "We often have slots the same week and sometimes the same day. Pick a date in the booking to see available times straight away.",
      },
      {
        question: "Can you move a whole house in Runby or Bollstanäs?",
        answer:
          "Yes. We take the whole house including basement, loft, garage and garden furniture. Large houses are booked as Extra Large or Mega Large, and we make several trips with the van if needed.",
      },
      {
        question: "Do you carry up and down stairs without a lift?",
        answer:
          "Yes. We carry in walk-up apartment blocks, on the internal stairs of terraced houses and down from lofts. Stairs cost nothing extra.",
      },
      {
        question: "What does it cost to move from Väsby to Stockholm?",
        answer:
          "The first 20 km are included. Upplands Väsby to the inner city is further than that, and the kilometres beyond are charged at the package's per-km rate shown in the price table and in the booking.",
      },
      {
        question: "Can you dismantle and reassemble furniture?",
        answer:
          "Yes, we take apart beds, wardrobes and tables that won't go out whole and put them back together at the new address.",
      },
      {
        question: "Do you move appliances?",
        answer:
          "Yes — washing machines, dryers, fridges, freezers and dishwashers. Empty and defrost the fridge and freezer at least a day before so nothing leaks in the van.",
      },
      {
        question: "Can you move at the weekend?",
        answer: "Yes, we move every day of the week, including Saturday and Sunday.",
      },
      {
        question: "Do you help businesses in Infra City move?",
        answer:
          "Yes, we do smaller office and storage moves in Infra City and the rest of Väsby: desks, office chairs, filing cabinets and boxes.",
      },
      {
        question: "Which parts of Upplands Väsby do you cover?",
        answer:
          "The whole municipality: central Väsby, Smedby, Ekebo, Carlslund, Grimstaby, Runby, Bollstanäs, Brunnby, Fresta, Sigma and Infra City.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `We help with moves across the Stockholm region: ${stockholmKommunerText}. Driving within 20 km is always included.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Upplands Väsby in 3 steps",
      step2Text:
        "The movers arrive with the van, protect anything fragile, carry out and load. Then we drive straight to the new address, in Väsby or wherever you're heading.",
      step3Title: "Moved in",
      step3Desc:
        "We carry everything in, put the furniture in the right rooms and reassemble what we took apart. All that's left for you is unpacking.",
      ctaHeading: "Book moving help in Upplands Väsby",
      ctaText:
        "Enter the old and new address, choose a package and a time. The price shows instantly and the RUT deduction comes off the invoice.",
    },
  },
};
